import type { CSSProperties } from 'react'

import { renderAs, type PolymorphicProps } from '@/components/motion/polymorphic'
import { staggerDelayStyle } from '@/lib/motion/stagger'
import { cn } from '@/lib/utils'

type Edge = 'left' | 'right' | 'bottom'

interface BarFillOwnProps {
  /**
   * How much of the track the fill covers, 0 to 1. Clamped. The fill is drawn
   * full size and scaled to it, so a refetch that changes it eases the bar
   * instead of replaying the grow. Leave it out for a bar whose own box sets
   * its length (a width, an inset pair, a flex share, an SVG rect's size): it
   * only grows in.
   */
  ratio?: number | undefined
  /** The edge it grows from: `right` for a bar anchored to a high, `bottom` for a column. */
  from?: Edge | undefined
  /** The mark's place in its list, for the 40ms stagger. */
  index?: number | undefined
  /** True once a chart's marks have entered (`useMarksEntered`), so a redraw cannot replay it. */
  entered?: boolean | undefined
  className?: string | undefined
  style?: CSSProperties | undefined
}

const GROW: Record<Edge, string> = {
  left: 'animate-grow-x',
  right: 'animate-grow-x',
  bottom: 'animate-grow-y',
}

const ORIGIN: Record<Edge, string> = {
  left: 'origin-left',
  right: 'origin-right',
  bottom: 'origin-bottom',
}

/**
 * The one way a bar grows: a div bar's fill, a segment, a column, an SVG rect.
 *
 * Scaling rather than animating a width keeps the grow-in on the compositor and
 * lets one keyframe (`grow-x` / `grow-y`, which animate from a zero scale to
 * whatever transform the element ends on) serve every length. It plays on
 * mount only; inside a table it plays once, with the table (TABLE MARKS,
 * globals.css). A ratio fill is square: a rounded end would stretch with the
 * scale.
 *
 * Hidden from assistive tech, since the figure is always printed beside it or
 * the bar sits in a labelled image — except an `li`, which is a list's item.
 */
export default function BarFill<T extends 'div' | 'span' | 'li' | 'rect' = 'div'>({
  as,
  ratio,
  from = 'left',
  index,
  entered = false,
  className,
  style,
  ...rest
}: PolymorphicProps<T, BarFillOwnProps>) {
  const scale =
    ratio === undefined
      ? undefined
      : `${from === 'bottom' ? 'scaleY' : 'scaleX'}(${String(Math.min(Math.max(ratio, 0), 1))})`
  const merged: CSSProperties = {
    ...(entered || index === undefined ? {} : staggerDelayStyle(index)),
    ...style,
    ...(scale === undefined ? {} : { transform: scale }),
  }

  return renderAs(as ?? 'div', {
    'aria-hidden': as === 'li' ? undefined : true,
    ...rest,
    className: cn(
      scale !== undefined && 'h-full w-full transition-transform duration-slow ease-out',
      !entered && GROW[from],
      ORIGIN[from],
      // An SVG shape scales from its own box, not the drawing's corner.
      as === 'rect' && 'transform-fill',
      className,
    ),
    style: Object.keys(merged).length === 0 ? undefined : merged,
  })
}
